import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { Search, Wrench, Layers, Paintbrush, CheckCircle, Sparkles } from 'lucide-react';

const processSteps = [
  {
    title: 'Inspection',
    description: 'Comprehensive assessment of your roof condition, identifying damage, wear, or potential issues.',
    icon: Search,
    image: '/images/process/step-1.jpg',
  },
  {
    title: 'Cleaning & Repairs',
    description: 'Thorough cleaning to remove dirt, debris, moss. Necessary repairs for leaks, cracks, or damaged shingles.',
    icon: Wrench,
    image: '/images/process/step-2.jpg',
  },
  {
    title: 'Priming',
    description: 'High-quality primer application for strong bond between roof material and paint.',
    icon: Layers,
    image: '/images/process/step-3.jpg',
  },
  {
    title: 'Painting',
    description: 'Premium-grade roof paint application with precision for even coverage and attractive finish.',
    icon: Paintbrush,
    image: '/images/process/step-4.jpg',
  },
  {
    title: 'Quality Assurance',
    description: 'Regular checks throughout to ensure each layer meets our stringent standards.',
    icon: CheckCircle,
    image: '/images/process/step-5.jpg',
  },
  {
    title: 'Clean Up',
    description: 'Meticulous cleanup, removing masking materials and ensuring a tidy work area.',
    icon: Sparkles,
    image: '/images/process/step-6.jpg',
  },
];

const Process = () => {
  const { theme } = useTheme();

  return (
    <section 
      id="process" 
      className={`${theme.layout.sectionSpacing} ${theme.colors.background}`}
      data-testid="process-section"
    >
      <div className={theme.layout.container}>
        {/* Header */}
        <div className="text-center mb-16">
          <span className={`${theme.typography.bodyFont} text-[#ff5218] uppercase tracking-widest text-sm font-semibold`}>
            How We Work
          </span>
          <h2 className={`
            ${theme.typography.headingFont} ${theme.typography.headingWeight}
            ${theme.colors.textPrimary} text-3xl md:text-4xl lg:text-5xl mt-4
            ${theme.typography.headingTracking} uppercase
          `}>
            Process of Roof Painting
          </h2>
        </div>

        {/* Process Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {processSteps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div 
                key={step.title}
                className={`relative group ${theme.components.card}`}
                data-testid={`process-${index}`}
              >
                {/* Step Number */}
                <div className="absolute -top-4 -left-4 w-10 h-10 flex items-center justify-center font-bold text-lg z-10 bg-[#ff5218] text-white">
                  {index + 1}
                </div>

                {/* Image */}
                <div className="relative h-40 overflow-hidden mb-6 -mx-8 -mt-8">
                  <img 
                    src={step.image}
                    alt={step.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-slate-900/60"></div>
                </div>

                {/* Content */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-slate-700 text-[#ff5218]">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className={`
                      ${theme.typography.headingFont} text-[#ff5218]
                      text-lg font-semibold mb-2 uppercase
                    `}>
                      {step.title}
                    </h3>
                    <p className={`${theme.typography.bodyFont} ${theme.colors.textSecondary} text-sm leading-relaxed`}>
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Process;
